import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import { format } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Custom components
import { useAuth } from '../../Contexts/AuthContext';
import { useSimulation } from '../../Contexts/SimulationContext';
import DataContext from '../../Contexts/DataContext';
import { getOrganizationProjectsList, getUserProjectsList, getUserJobs, createChange, archiveProject, restoreProject } from '../../Helpers/dataApi';
import useCookie from '../../Helpers/cookies';
import SupportContactMessage from '../SupportContactMessage';
import { useProjectCreationSocket } from '../../Helpers/useProjectCreationSocket';
import ArchivedProjectCard from './ArchivedProjectCard';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { filterProjects } from '../../Helpers/archiveUtils';
import { clearSavedFilters } from '../../Helpers/filterPersistence';

/**
 * Card for a project that is currently being created.
 * Shows real-time progress from the WebSocket/polling and a cancel button with confirmation.
 */
const PendingProjectCard = ({ project, onCancelled, onCompleted }) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [creepProgress, setCreepProgress] = useState(null);
  const creepRef = useRef(null);

  // For preview mode (fake job ID), use mock progress state instead of the socket
  const isPreview = project.jobId === 'preview-fake-job-id';
  const [mockProgress] = useState(42);
  const mockStep = 'Creating accounts';
  const mockDetail = '84,000 / 200,000 accounts processed';

  const { progress, status, step, detail, cancelJob } = useProjectCreationSocket(
    isPreview ? null : project.jobId,
    {
      onCompleted: (data) => { if (onCompleted) onCompleted(data.project_id); },
      onFailed: () => { if (onCancelled) onCancelled(); },
    }
  );

  // Also handle status transitions (e.g. polling catches completion before callback fires)
  useEffect(() => {
    if (status === 'completed') { if (onCompleted) onCompleted(null); }
    if (status === 'failed' || status === 'cancelled') { if (onCancelled) onCancelled(); }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Simulated progress creep: when real progress is stuck at a low value for a long time
  // (e.g. during S3 download/parse), slowly advance toward a cap so the bar looks alive.
  useEffect(() => {
    if (isPreview) return;
    const realProgress = progress || 0;
    
    // Only creep when real progress is low and we're in a known slow phase
    const slowPhases = ['Downloading file from S3', 'Parsing Excel file', 'Initializing'];
    const isSlowPhase = slowPhases.some(p => (step || '').includes(p));
    
    if (isSlowPhase && realProgress < 15) {
      // Start creep from current real progress toward 14% (just under the next real update)
      if (creepRef.current) clearInterval(creepRef.current);
      let current = creepProgress !== null ? creepProgress : realProgress;
      const cap = 14;
      creepRef.current = setInterval(() => {
        current = Math.min(cap, current + Math.max(0.05, (cap - current) * 0.03));
        setCreepProgress(current);
      }, 500);
    } else {
      // Real progress has moved past the slow phase — stop creep and use real value
      if (creepRef.current) { clearInterval(creepRef.current); creepRef.current = null; }
      setCreepProgress(null);
    }
    
    return () => { if (creepRef.current) clearInterval(creepRef.current); };
  }, [progress, step, isPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayProgress = isPreview ? mockProgress : (creepProgress !== null ? creepProgress : (progress || 0));
  const displayStep = isPreview ? mockStep : (step || 'Initializing...');
  const displayDetail = isPreview ? mockDetail : detail;

  const handleCancelConfirmed = async () => {
    setShowCancelConfirm(false);
    try {
      await cancelJob();
    } catch (err) {
      console.error('Failed to cancel job:', err);
    }
    if (onCancelled) onCancelled();
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px dashed',
          borderColor: 'info.main',
          backgroundColor: 'rgba(2, 136, 209, 0.04)',
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {project.title}
            </Typography>
            <Chip label="CREATING" color="info" size="small" sx={{ fontWeight: 600, flexShrink: 0 }} />
          </Box>

          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{displayStep || 'Initializing...'}</Typography>
              <Typography variant="caption" color="text.secondary">{Math.round(displayProgress)}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={displayProgress} sx={{ height: 6, borderRadius: 3 }} />
            {displayDetail && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {displayDetail}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{project.created_by}</Typography>
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
          <Tooltip title="Cancel project creation">
            <IconButton size="small" color="error" onClick={() => setShowCancelConfirm(true)}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>

      {/* Cancel confirmation */}
      <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Project Creation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel creating <strong>{project.title}</strong>? This cannot be undone and any progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)} color="inherit">Keep Going</Button>
          <Button onClick={handleCancelConfirmed} color="error" variant="contained">Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ProjectSelector = (props) => {
  
  const history = useHistory();
  const auth = useAuth();
  const simulation = useSimulation();
  const activeUser = auth.activeUser;
  let {
    activeProjectInfo,
    deleteProject,
    setActiveProject,
  } = useContext(DataContext);

  const activeIcon = <OnlinePredictionIcon
                      color={'success'}
                      sx={{
                        mr: '1rem',
                        position: 'relative',
                        top: '-1px',
                      }}
                    />

  const getProjects = async () => {
    const effectiveRole = simulation.getEffectiveRole();
    const effectiveOrg = simulation.getEffectiveOrganization();
    
    setIsLoading(true);
    
    if(effectiveRole === 0 || effectiveRole === 1){
      // Admin and Super User: Get all projects in organization
      await getOrganizationProjectsList(effectiveOrg.id, activeUser.accessToken)
        .then((response) => {
          if(response.status === 200){
            setProjects(response.data?.active || []);
            setArchivedProjects(response.data?.archived || []);
          }
          else{
            setError({...error, getProjects: true});
            setErrorMessage(`status ${response.status}`);
            console.error(response);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setError({...error, getProjects: true});
          setErrorMessage(`${err}`);
          setIsLoading(false);
        });
    }
    else{
      // Contributor/Observer: Get projects associated with activeUser
      getUserProjectsList(activeUser.id, effectiveOrg.id, activeUser.accessToken)
        .then((response) => {
          if (response && response.status === 200) {
            setProjects(response.data?.active || []);
            setArchivedProjects(response.data?.archived || []);
          } else {
            setError({ ...error, getProjects: true });
            setErrorMessage(`status ${response?.status}`);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setError({ ...error, getProjects: true });
          setErrorMessage(`${err}`);
          setIsLoading(false);
        });
    }
  }

  const [activeProjectId, update, remove] = useCookie("activeProjectId", false);
  const [isLoading, setIsLoading] = useState(props.isLoading);
  const [loadingProjectId, setLoadingProjectId] = useState(null); // Track which project is loading
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [pendingProject, setPendingProject] = useState(null); // Track project being created
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogMode, setDeleteDialogMode] = useState(null);
  const [projectToAction, setProjectToAction] = useState(null);
  const [error, setError] = useState({
    delete: false,
    getProjects: false,
    setActiveProject: false,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getProjects();
    
    // Check if we're navigating here with a new project
    if (history.location.state?.newProject) {
      const newProj = history.location.state.newProject;
      setPendingProject({
        id: `pending-${newProj.jobId}`,
        title: newProj.title,
        created_on: newProj.createdOn,
        created_by: newProj.createdBy,
        jobId: newProj.jobId,
        isPending: true,
        pendingChanges: newProj.pendingChanges ?? null,
      });
      
      // Clear the navigation state to prevent showing it again on refresh
      history.replace({ ...history.location, state: {} });
    } else {
      // On refresh or direct navigation, check the jobs API for any active jobs
      // This ensures the pending card survives a page refresh
      getUserJobs(activeUser.accessToken, 5).then(response => {
        if (response?.data?.jobs) {
          const activeJob = response.data.jobs.find(
            j => j.status === 'in_progress' || j.status === 'pending'
          );
          if (activeJob) {
            setPendingProject({
              id: `pending-${activeJob.job_id}`,
              title: activeJob.project_title,
              created_on: activeJob.created_at,
              created_by: activeUser.email || activeUser.username || '',
              jobId: activeJob.job_id,
              isPending: true,
            });
          }
        }
      }).catch(() => {/* silently ignore — jobs API failure shouldn't break the page */});
    }
  }, [activeUser, simulation.simulatedRole, simulation.simulatedOrganization]);
  
  // Poll for job completion and refresh projects when done — handled by PendingProjectCard via useProjectCreationSocket

  // Combine real projects with pending project for display
  const allProjects = useMemo(() => {
    if (pendingProject) {
      return [pendingProject, ...projects];
    }
    return projects;
  }, [projects, pendingProject]);

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    return filterProjects(searchQuery, allProjects);
  }, [allProjects, searchQuery]);

  const filteredArchivedProjects = useMemo(() => {
    return filterProjects(searchQuery, archivedProjects);
  }, [archivedProjects, searchQuery]);

  const handleProjectSelected = async (project) => {
    // Debug logging
    if (process.env.NODE_ENV !== 'production') {
      console.log('ProjectSelector: handleProjectSelected', {
        projectId: project.id,
        projectTitle: project.title,
        projectOrgId: project.organization_id,
        effectiveOrg: simulation.getEffectiveOrganization(),
        activeProjectInfo: activeProjectInfo?.id,
      });
    }
    
    // Check if activeProjectInfo exists and has an id before comparing
    if(!activeProjectInfo || activeProjectInfo.id !== project.id){
      update('activeProjectId', project.id);
      setLoadingProjectId(project.id); // Show loading on this specific card
      
      try {
        await setActiveProject(project.id, activeUser.accessToken);
        
        if (process.env.NODE_ENV !== 'production') {
          console.log('ProjectSelector: Project loaded successfully, navigating to project-home');
        }
        
        // Only navigate after successful load
        history.push('/project-home');
      } catch (err) {
        console.error('Failed to set active project:', err);
        setLoadingProjectId(null); // Clear loading state on error
        setError({
          ...error,
          setActiveProject: true
        });
        setErrorMessage(`Failed to load project: ${err.message}`);
      }
    }
    else{
      if (process.env.NODE_ENV !== 'production') {
        console.log('ProjectSelector: Project already active, navigating to project-home');
      }
      history.push('/project-home');
    }
  }

  // Determine effective role and handle delete click
  const handleDeleteClick = (project) => {
    const isPrivileged = simulation.getEffectiveRole() <= 1; // Admin or Super User
    setProjectToAction(project);
    setDeleteDialogMode(isPrivileged ? 'active-privileged' : 'active-contributor');
    setDeleteDialogOpen(true);
  };

  const handleArchiveConfirmed = async () => {
    setDeleteDialogOpen(false);
    const project = projectToAction;
    setProjectToAction(null);
    const response = await archiveProject(project.id, activeUser.accessToken);
    if (response?.status === 200) {
      setProjects(prev => prev.filter(p => p.id !== project.id));
      setArchivedProjects(prev => [response.data, ...prev]);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 10000);
    } else {
      setError({ ...error, delete: true });
      setErrorMessage(response?.response?.data?.errors || 'Failed to archive project');
    }
  };

  const handlePermanentDeleteConfirmed = async () => {
    setDeleteDialogOpen(false);
    const project = projectToAction;
    setProjectToAction(null);
    setIsLoading(true);
    const response = await deleteProject(project, activeUser.accessToken);
    if (response?.success) {
      clearSavedFilters(project.id); // remove persisted filter state for the deleted project
      setProjects(prev => prev.filter(p => p.id !== project.id));
      setArchivedProjects(prev => prev.filter(p => p.id !== project.id));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 10000);
      setError({ ...error, delete: false });
    } else {
      setError({ ...error, delete: true });
      setErrorMessage(response?.error || 'Failed to delete project');
    }
    setIsLoading(false);
  };

  const handleRestoreProject = async (project) => {
    const response = await restoreProject(project.id, activeUser.accessToken);
    if (response?.status === 200) {
      setArchivedProjects(prev => prev.filter(p => p.id !== project.id));
      setProjects(prev => [response.data, ...prev]);
    } else {
      setError({ ...error, delete: true });
      setErrorMessage(response?.response?.data?.errors || 'Failed to restore project');
    }
  };

  const handleArchivedDeleteClick = (project) => {
    setProjectToAction(project);
    setDeleteDialogMode('archived-privileged');
    setDeleteDialogOpen(true);
  };

  return (
    <Grid 
      id={'project-selector'}
      item
      xs={12}
    >
      <Paper
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 400,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography
            component='h2'
            variant='h5'
            color='inherit'
            sx={{ fontWeight: 600 }}
          >
            Projects
          </Typography>
          <Tooltip
            arrow
            placement={'left'}
            title={'Create new Project'}
            TransitionComponent={Zoom}
          >
            <IconButton
              aria-label="new-project"
              color="success"
              size="large"
              onClick={() => {
                history.push('/new-project');
              }}
              sx={{ ml: 'auto' }}
            >
              <AddCircleIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Search Bar */}
        {!isLoading && (projects.length > 0 || archivedProjects.length > 0) && (
          <TextField
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            size="small"
          />
        )}

        {/* Loading Skeletons */}
        {isLoading && (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((n) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={n}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Project Cards */}
        {!isLoading && filteredProjects.length > 0 && (
          <Grid container spacing={2}>
            {filteredProjects.map((project) => {
              const isActive = project.id === activeProjectId;
              const isLoadingThisProject = loadingProjectId === project.id;
              const isPending = project.isPending || false;
              
              // Check if project was created within last 24 hours
              const createdDate = new Date(project.created_on);
              const now = new Date();
              const hoursSinceCreation = (now - createdDate) / (1000 * 60 * 60);
              const isNew = hoursSinceCreation <= 24;
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
                  {isPending ? (
                    <PendingProjectCard
                      project={project}
                      onCancelled={() => setPendingProject(null)}
                      onCompleted={async (projectId) => {
                        // If this project was created from an Excel export, import
                        // the changes now that we have a real project ID.
                        if (projectId && pendingProject?.pendingChanges?.length) {
                          const changes = pendingProject.pendingChanges;
                          const accessToken = activeUser.accessToken;
                          for (const change of changes) {
                            try {
                              await createChange(change, activeUser.id, projectId, accessToken);
                            } catch (err) {
                              console.error('[ExcelImport] Failed to import change:', change.change_label, err);
                            }
                          }
                        }
                        setPendingProject(null);
                        await getProjects();
                      }}
                    />
                  ) : (
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: isActive ? '2px solid' : '1px solid',
                      borderColor: isActive ? 'success.main' : 'divider',
                      backgroundColor: isNew ? 'rgba(76, 175, 80, 0.04)' : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {/* Loading Overlay (when opening a project) */}
                    {isLoadingThisProject && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0, left: 0, right: 0, bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          zIndex: 2,
                          borderRadius: 1,
                        }}
                      >
                        <CircularProgress size={40} />
                      </Box>
                    )}
                    {/* Active Badge */}
                    {isActive && (
                      <Chip
                        icon={<OnlinePredictionIcon />}
                        label="Active"
                        color="success"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                        }}
                      />
                    )}
                    {/* NEW Badge */}
                    {isNew && !isActive && (
                      <Chip
                        label="NEW"
                        color="success"
                        size="small"
                        variant="outlined"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <CardActionArea
                      onClick={() => handleProjectSelected(project)}
                      disabled={isLoadingThisProject || isPending}
                      sx={{ flexGrow: 1 }}
                    >
                      <CardContent>
                        <Typography
                          variant="h6"
                          component="div"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            pr: isActive ? 8 : 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {project.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {project.created_by}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(project.created_on), 'MMM d, yyyy')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                      <Tooltip title="Delete project">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(project)}
                          disabled={isLoadingThisProject || isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                  )}
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Archived Projects Section */}
        {!isLoading && filteredArchivedProjects.length > 0 && (
          <>
            <Typography
              variant="h6"
              sx={{ mt: 4, mb: 1, fontWeight: 600, color: 'text.secondary' }}
            >
              Archived Projects
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {filteredArchivedProjects.map((project) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
                  <ArchivedProjectCard
                    project={project}
                    effectiveRole={simulation.getEffectiveRole()}
                    onRestore={handleRestoreProject}
                    onDelete={handleArchivedDeleteClick}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Empty State */}
        {!isLoading && projects.length === 0 && archivedProjects.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Get started by creating your first project
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddCircleIcon />}
              onClick={() => history.push('/new-project')}
            >
              Create Project
            </Button>
          </Box>
        )}

        {/* No Search Results */}
        {!isLoading && (projects.length > 0 || archivedProjects.length > 0) && filteredProjects.length === 0 && filteredArchivedProjects.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <SearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects match your search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search terms
            </Typography>
          </Box>
        )}

        {/* Success/Error Messages */}
        <Collapse 
          in={success}
          sx={{ mt: 2 }}
        >
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setSuccess(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            Action completed successfully
          </Alert>
        </Collapse>
        <Collapse
          in={error.delete || error.setActiveProject}
          sx={{ mt: 2 }}
        >
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError({
                    setActiveProject: false,
                    delete: false,
                  })
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity="error"
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Project action failed. Please refresh the page and try again.
            </Typography>
            <SupportContactMessage 
              errorMessage={errorMessage}
              subject={error.delete ? "Error deleting a project" : "Error loading project"}
              context={{
                action: error.delete ? 'delete' : 'load',
                projectId: error.delete ? projectToAction?.id : activeProjectId
              }}
            />
          </Alert>
        </Collapse>
      </Paper>
      
      {/* Active Project Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, pl: 2 }}>
        <OnlinePredictionIcon color="success" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Active project
        </Typography>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setProjectToAction(null);
        }}
        onArchive={handleArchiveConfirmed}
        onDeletePermanently={handlePermanentDeleteConfirmed}
        project={projectToAction}
        mode={deleteDialogMode}
      />
    </Grid>
  );
};

export default ProjectSelector;